import React, { useState, useEffect } from "react";
import { X, Trash2, Plus, Info } from "lucide-react";
import { useSelector } from "react-redux";

interface CapacityRow {
  id: string;
  startDate: string;
  endDate: string;
  type: "Solar" | "Wind" | "Hydro" | "Thermal" | "Biomass" | "Bagasse" | "Gas" | "Nuclear" | "Hybrid" | "Small Hydro";
  quantum: string;
  pf: string; // Power Factor
  vc: string; // Variable Cost
  fc: string; // Fixed Cost
}

interface AddCapacityModalProps {
  isModalOpen: boolean;
  setIsModalOpen: (open: boolean) => void;
  modalTitle?: string;
  onSubmit: (data: CapacityRow[]) => void;
  loading: any;
  setLoading: any;
}

const AddCapacityModal: React.FC<AddCapacityModalProps> = ({
  isModalOpen,
  setIsModalOpen,
  modalTitle = "Add Capacity",
  onSubmit,
  loading,
  setLoading,
}) => {

  const SelectedStartDate = useSelector(
    (state: any) => state.SurplusOptimizerHPPCDate.startDate
  );
  const SelectedEndDate = useSelector(
    (state: any) => state.SurplusOptimizerHPPCDate.endDate
  );

  const [capacities, setCapacities] = useState<CapacityRow[]>([
    {
      id: "1",
      startDate: "",
      endDate: "",
      type: "Solar",
      quantum: "",
      pf: "",
      vc: "",
      fc: "",
    },
  ]);

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [showErrors, setShowErrors] = useState(false);

  const validateCapacities = () => {
    const newErrors: Record<string, string> = {};

    capacities.forEach((capacity) => {
      if (!capacity.startDate) {
        newErrors[`${capacity.id}-startDate`] = "Required";
      }
      if (!capacity.endDate) {
        newErrors[`${capacity.id}-endDate`] = "Required";
      }
      if (!capacity.quantum) {
        newErrors[`${capacity.id}-quantum`] = "Required";
      }
      if (!capacity.pf) {
        newErrors[`${capacity.id}-pf`] = "Required";
      }
      if (!capacity.vc) {
        newErrors[`${capacity.id}-vc`] = "Required";
      }
      // ❌ FC validation removed
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleAddCapacity = () => {
    const newCapacity: CapacityRow = {
      id: Date.now().toString(),
      startDate: "",
      endDate: "",
      type: "Solar",
      quantum: "",
      pf: "",
      vc: "",
      fc: "",
    };
    setCapacities([...capacities, newCapacity]);
  };

  const handleDeleteCapacity = (id: string) => {
    if (capacities.length > 1) {
      setCapacities(capacities.filter((capacity) => capacity.id !== id));
    }
  };

  const handleUpdateCapacity = (id: string, field: string, value: any) => {
    setCapacities(
      capacities.map((capacity) => {
        if (capacity.id === id) {
          // If updating start date and end date is before it, clear end date
          if (field === "startDate" && capacity.endDate && value > capacity.endDate) {
            return { ...capacity, [field]: value, endDate: "" };
          }
          return { ...capacity, [field]: value };
        }
        return capacity;
      })
    );
    if (errors[`${id}-${field}`]) {
      const newErrors = { ...errors };
      delete newErrors[`${id}-${field}`];
      setErrors(newErrors);
    }
  };

  const handleSubmit = () => {
    setShowErrors(true);
    if (validateCapacities()) {
      onSubmit(capacities);
      setShowErrors(false);
      setLoading(true);
      // Reset to initial state after submit
      setCapacities([
        {
          id: "1",
          startDate: "",
          endDate: "",
          type: "Solar",
          quantum: "",
          pf: "",
          vc: "",
          fc: "",
        },
      ]);
      setErrors({});
    }
  };

  const handleClose = () => {
    setIsModalOpen(false);
    setShowErrors(false);
    // Reset to initial state on close
    setCapacities([
      {
        id: "1",
        startDate: "",
        endDate: "",
        type: "Solar",
        quantum: "",
        pf: "",
        vc: "",
        fc: "",
      },
    ]);
    setErrors({});
  };

  if (!isModalOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[99] p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="px-4 py-2 border-b border-gray-200 flex items-center justify-between bg-white">
          <h2 className="text-base font-medium text-[#525062]">{modalTitle}</h2>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
            aria-label="Close modal"
          >
            <X strokeWidth={3} size={18} />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-4 py-4 bg-[#F3F5F8]">
          {showErrors && Object.keys(errors).length > 0 && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-red-600 text-xs font-medium">
                Please fill all required fields
              </p>
            </div>
          )}
          <div className="space-y-4">
            {capacities.map((capacity, capacityIndex) => (
              <div key={capacity.id} className="bg-white rounded-lg">
                {/* Header with Capacity Number and Delete */}
                <div className="flex items-center justify-between w-[100%] px-3 py-2 mb-2 border-b-2 border-[#DFEAF2]">
                  <h3 className="text-xs font-medium text-[#34656D]">
                    Capacity {capacityIndex + 1}
                  </h3>
                  <div className="flex gap-2 mr-3">
                    <button
                      onClick={() => handleDeleteCapacity(capacity.id)}
                      className="p-1 rounded-lg border border-[#E2E2E5] bg-[#F3F3F4] cursor-pointer hover:bg-gray-100 transition-colors text-gray-400"
                      aria-label="Delete capacity"
                      disabled={capacities.length === 1}
                    >
                      <Trash2 strokeWidth={3} size={16} />
                    </button>
                    <button
                      onClick={handleAddCapacity}
                      className="p-1 rounded-lg border border-[#F1935C] bg-[#FFFAF6] cursor-pointer hover:bg-orange-50 transition-colors text-[#FF8C42]"
                      aria-label="Add capacity"
                    >
                      <Plus strokeWidth={3} size={16} />
                    </button>
                  </div>
                </div>
                <div className="px-3 py-2">
                  {/* First Row: Type, Start Date, End Date */}
                  <div className="grid grid-cols-3 gap-3 mb-4">
                    <div>
                      <label className="block text-xs font-medium mb-1 text-[#6F6D7C]">
                        Type <span className="text-red-500">*</span>
                      </label>
                      <select
                        value={capacity.type}
                        onChange={(e) =>
                          handleUpdateCapacity(capacity.id, "type", e.target.value)
                        }
                        className="w-full px-2 py-1 !h-[32px] border border-[#E2E2E5] rounded-lg text-xs focus:outline-none focus:ring-2 focus:ring-orange-500 bg-white cursor-pointer text-gray-700"
                      >
                        <option value="Solar">Solar</option>
                        <option value="Wind">Wind</option>
                        <option value="Hydro">Hydro</option>
                        <option value="Thermal">Thermal</option>
                        <option value="Biomass">Biomass</option>
                        <option value="Bagasse">Bagasse</option>
                        <option value="Gas">Gas</option>
                        <option value="Nuclear">Nuclear</option>
                        <option value="Hybrid">Hybrid</option>
                        <option value="Small Hydro">Small Hydro</option>
                      </select>
                    </div>

                    <div>
                      <label className="flex items-center gap-1 text-xs font-medium mb-1 text-[#6F6D7C]">
                        Start Date <span className="text-red-500">*</span>
                        <Info size={12} className="text-gray-400" />
                      </label>
                      <input
                        type="date"
                        value={capacity.startDate}
                        onChange={(e) =>
                          handleUpdateCapacity(
                            capacity.id,
                            "startDate",
                            e.target.value
                          )
                        }
                        min={SelectedStartDate}
                        max={SelectedEndDate}
                        className={`w-full px-2 py-1.5 border rounded-lg text-xs focus:outline-none focus:ring-2 focus:ring-orange-500 bg-white text-gray-700 ${showErrors && errors[`${capacity.id}-startDate`]
                          ? "border-red-500"
                          : "border-[#E2E2E5]"
                          }`}
                      />
                    </div>

                    <div>
                      <label className="block text-xs font-medium mb-1 text-[#6F6D7C]">
                        End Date <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="date"
                        value={capacity.endDate}
                        onChange={(e) =>
                          handleUpdateCapacity(capacity.id, "endDate", e.target.value)
                        }
                        min={
                          capacity.startDate ? capacity.startDate : SelectedStartDate
                        }
                        max={SelectedEndDate}
                        className={`w-full px-2 py-1.5 border rounded-lg text-xs focus:outline-none focus:ring-2 focus:ring-orange-500 text-gray-700 ${showErrors && errors[`${capacity.id}-endDate`]
                          ? "border-red-500"
                          : "border-[#E2E2E5]"
                          }`}
                      />
                    </div>
                  </div>

                  {/* Second Row: Quantum, PLF, VC, FC */}
                  <div className="grid grid-cols-4 gap-3">
                    <div>
                      <label className="block text-xs font-medium mb-1 text-[#6F6D7C]">
                        Quantum (MW) <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="number"
                        value={capacity.quantum}
                        onChange={(e) =>
                          handleUpdateCapacity(
                            capacity.id,
                            "quantum",
                            e.target.value
                          )
                        }
                        placeholder="Enter quantum"
                        className={`w-full px-2 py-1.5 border rounded-lg text-xs focus:outline-none focus:ring-2 focus:ring-orange-500 bg-white text-gray-700 ${showErrors && errors[`${capacity.id}-quantum`]
                          ? "border-red-500"
                          : "border-[#E2E2E5]"
                          }`}
                      />
                    </div>

                    <div>
                      <label className="block text-xs font-medium mb-1 text-[#6F6D7C]">
                        PLF <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="number"
                        step="0.01"
                        value={capacity.pf}
                        onChange={(e) =>
                          handleUpdateCapacity(capacity.id, "pf", e.target.value)
                        }
                        placeholder="Power Factor"
                        className={`w-full px-2 py-1.5 border rounded-lg text-xs focus:outline-none focus:ring-2 focus:ring-orange-500 bg-white text-gray-700 ${showErrors && errors[`${capacity.id}-pf`]
                          ? "border-red-500"
                          : "border-[#E2E2E5]"
                          }`}
                      />
                    </div>

                    <div>
                      <label className="block text-xs font-medium mb-1 text-[#6F6D7C]">
                        VC (₹/kWh) <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="number"
                        step="0.01"
                        value={capacity.vc}
                        onChange={(e) =>
                          handleUpdateCapacity(capacity.id, "vc", e.target.value)
                        }
                        placeholder="Variable Cost"
                        className={`w-full px-2 py-1.5 border rounded-lg text-xs focus:outline-none focus:ring-2 focus:ring-orange-500 bg-white text-gray-700 ${showErrors && errors[`${capacity.id}-vc`]
                          ? "border-red-500"
                          : "border-[#E2E2E5]"
                          }`}
                      />
                    </div>

                    <div>
                      <label className="block text-xs font-medium mb-1 text-[#6F6D7C]">
                        FC (₹/kW)
                      </label>

                      <div className="w-full px-2 py-1.5 border rounded-lg bg-gray-50 text-xs text-gray-700 border-[#E2E2E5]">
                        {capacity.fc || "-"}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Footer */}
        <div className="px-4 py-3 border-t border-gray-200 flex justify-center gap-3 bg-white">
          <button
            onClick={handleClose}
            className="flex items-center justify-center px-6 py-1.5 border border-[#8C8A96] text-[#8C8A96] text-sm rounded-xl font-medium hover:opacity-90 transition-opacity disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={loading}
            className="flex items-center justify-center px-6 py-1.5 bg-[#F1935C] text-white text-sm rounded-xl font-medium hover:opacity-90 transition-opacity disabled:opacity-50"
          >
            {loading ? "Loading..." : "Submit"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AddCapacityModal;
export { AddCapacityModal };
export type { CapacityRow };